import { Flex, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  type FieldStatusDto,
  type SourcingEventV2Dto,
} from "../../../../../../autogen/bff-api";
import type { ReadinessLookup, KnownFieldKey } from "../../hooks/useReadinessByField";
import type { PublicEventStep } from "../../wizardSteps";
import { MilestoneTimeline } from "../MilestoneTimeline";
import { ReadinessBadge } from "./ReadinessBadge";
import { SummarySection } from "./SummarySection";

export const FIELD_KEYS = [
  "BidSubmissionDeadlinePresent",
  "BidSubmissionDeadlineAboveMinimum",
  "BidSubmissionDeadlineConfirmed",
] as const satisfies readonly KnownFieldKey[];

const worseReadiness = (
  ...statuses: (FieldStatusDto | undefined)[]
): FieldStatusDto | undefined =>
  statuses.find((s) => s && !s.satisfied) ?? statuses.find((s) => s !== undefined);

interface Props {
  event: SourcingEventV2Dto;
  readinessFor: ReadinessLookup;
  onWizardStepNavigate: (step: PublicEventStep) => void;
}

export const DeadlinesAndMilestonesSection = ({ event, readinessFor, onWizardStepNavigate }: Props) => {
  const { t } = useTranslation();
  const { eventData, publicContext } = event;
  const deadline = eventData.bidSubmissionDeadline ?? null;

  const fieldStatuses = FIELD_KEYS.map(readinessFor).filter(
    (s): s is FieldStatusDto => s !== undefined,
  );

  return (
    <SummarySection
      title={t("Deadlines and milestones")}
      editTarget={{ kind: "wizard-step", step: "procedure" }}
      fieldStatuses={fieldStatuses}
      onWizardStepNavigate={onWizardStepNavigate}
    >
      <MilestoneTimeline
        mode="summary"
        event={event}
        deadlineBadge={
          <ReadinessBadge
            status={worseReadiness(
              readinessFor("BidSubmissionDeadlinePresent"),
              readinessFor("BidSubmissionDeadlineAboveMinimum"),
              readinessFor("BidSubmissionDeadlineConfirmed"),
            )}
          />
        }
      />

      {deadline && publicContext.eFormsNoticeData?.tenderValidityMonths != null && (
        <Flex gap="4" mt="5" wrap="wrap">
          <Text fontSize="xs" color="smMuted">
            {t("Tender validity")}:{" "}
            <b>{t("{{count}} months", { count: publicContext.eFormsNoticeData.tenderValidityMonths })}</b>
          </Text>
        </Flex>
      )}
    </SummarySection>
  );
};
